
import { useState } from "react";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Image, Upload, Loader2, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage } from "@/utils/entryEditor";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EntryImagesSectionProps {
  images: string[];
  setImages: React.Dispatch<React.SetStateAction<string[]>>;
}

const EntryImagesSection = ({ images, setImages }: EntryImagesSectionProps) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [imageToDelete, setImageToDelete] = useState<{ url: string; index: number } | null>(null);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user?.id) {
      toast.error("You need to be logged in to upload images");
      return;
    }
    
    const file = event.target.files?.[0];
    if (!file) {
      toast.error("No file selected");
      return;
    }
    
    setUploadError(null);
    setIsUploading(true);
    
    try {
      console.log("Starting image upload for diary entry:", file.name);
      // This will both upload the image and save it to the gallery
      const imageUrl = await uploadImage(file, user.id, language);
      
      if (imageUrl) {
        console.log("Upload successful, adding to entry:", imageUrl);
        setImages(prevImages => [...prevImages, imageUrl]);
        toast.success(t('gallery.photoUploadedSuccess', language));
      } else {
        console.error("Upload failed: No URL returned");
        setUploadError(t('gallery.photoUploadFailed', language));
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setUploadError(`${t('gallery.photoUploadFailed', language)}: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };
  
  const removeImage = (indexToRemove: number) => {
    setImages(images.filter((_, index) => index !== indexToRemove));
  };

  const confirmImageDelete = (index: number) => {
    setImageToDelete({ url: images[index], index });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <FormLabel className="mb-0">{t('generate.form.addPhotos', language)}</FormLabel>
        <div className="flex items-center">
          <Input
            id="image-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
            disabled={isUploading}
          />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={() => document.getElementById('image-upload')?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> 
                {t('gallery.uploading', language)}
              </div>
            ) : (
              <div className="flex items-center">
                <Upload className="h-4 w-4 mr-2" /> 
                {t('gallery.addPhoto', language)}
              </div>
            )}
          </Button>
        </div>
      </div>
      
      {uploadError && (
        <Alert variant="destructive" className="py-2">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      )}
      
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
          {images.map((imageUrl, index) => (
            <div key={index} className="relative group">
              <img 
                src={imageUrl} 
                alt={`Entry image ${index + 1}`} 
                className="h-24 w-full object-cover rounded-md border"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => confirmImageDelete(index)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      {images.length === 0 && (
        <div className="flex flex-col items-center justify-center p-6 border border-dashed rounded-md">
          <Image className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">{t('gallery.noPhotos', language)}</p>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!imageToDelete} onOpenChange={(open) => !open && setImageToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gallery.deletePhoto', language)}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('gallery.deletePhotoConfirm', language)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common.cancel', language)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (imageToDelete) {
                  removeImage(imageToDelete.index);
                }
                setImageToDelete(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('gallery.deletePhoto', language)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EntryImagesSection;
